// components/HomeProjectsShowcase.tsx
"use client";

import * as React from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import ProjectsCarousel from "@/components/ProjectsCarousel";
import ProjectModal from "@/components/ProjectModal";
import { projects, type Project } from "@/constants/projects";


export default function HomeProjectsShowcase() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [opened, setOpened] = React.useState(false);
  const [selectedProject, setSelectedProject] = React.useState<Project | null>(null);

  // Open modal + push ?project=ID (so Back works)
  const openProject = React.useCallback(
    (project: Project) => {
      setSelectedProject(project);
      setOpened(true);
      const next = `${pathname}?project=${encodeURIComponent(String(project.id))}`;
      router.push(next, { scroll: false });
    },
    [pathname, router]
  );

  // Close modal + remove ?project
  const closeProject = React.useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("project");
    const next = params.toString() ? `${pathname}?${params}` : pathname;
    router.replace(next, { scroll: false });
    setOpened(false);
    setSelectedProject(null);
  }, [pathname, router, searchParams]);

  React.useEffect(() => {
    const idFromUrl = searchParams.get("project");
    if (!idFromUrl) {
      setOpened(false);
      setSelectedProject(null);
      return;
    }

    const found = projects.find((p) => p.id === idFromUrl);

    if (found) {
      setSelectedProject(found);
      setOpened(true);
    } else {
      // invalid id -> clean URL
      closeProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <ProjectsCarousel projects={projects} onCardClick={openProject} />

      <ProjectModal opened={opened} onClose={closeProject} project={selectedProject} />
    </>
  );
}
